// Global Instructions Rule Applied!

import { API_ROUTES, buildFullUrl, getBackendBaseUrl } from '../../../core/utils/endpoints.ts'
import {
  IndustryGridModel,
  validateIndustryGridModel
} from '../../../state_manager/data_models/industry_grid_model.ts'
import { readJsonBody } from '../../../core/utils/utilities.ts'

const BACKEND_BASE_URL = getBackendBaseUrl()

export interface IndustrySort {
  key: string
  direction: 'asc' | 'desc'
}
export interface IndustryFilter {
  key: string
  value: unknown
}

export interface IndustriesResponse {
  data: IndustryGridModel[]
  hasMore: boolean
  totalCount: number
  error?: string
}

export interface WriteResponse {
  success: boolean
  data?: unknown
  error?: string
}

const authHeaders = (accessToken: string | null): Record<string, string> => {
  const headers: Record<string, string> = { 'Content-Type': 'application/json' }
  if (accessToken) headers['Authorization'] = `Bearer ${accessToken}`
  return headers
}

const errorMessage = async (response: Response, fallback: string): Promise<string> => {
  if (response.status === 401) return 'Authentication failed - please log in again'
  const body = await readJsonBody<{ message?: string }>(response)
  return body?.message || `${fallback}: ${response.status} ${response.statusText}`
}

export const fetchIndustries = async (
  sorting: IndustrySort[] = [],
  filters: IndustryFilter[] = [],
  page: number = 1,
  pageSize: number = 1000,
  accessToken: string | null = null
): Promise<IndustriesResponse> => {
  try {
    const requestBody = {
      data: {
        page,
        page_size: pageSize,
        sorting: sorting.map((s) => ({ key: s.key, direction: s.direction })),
        filters: filters.map((f) => ({ key: f.key, value: f.value }))
      }
    }

    const response = await fetch(buildFullUrl(BACKEND_BASE_URL, API_ROUTES.CONFIG.INDUSTRIES_FILTER), {
      method: API_ROUTES.CONFIG.INDUSTRIES_FILTER.method,
      headers: authHeaders(accessToken),
      body: JSON.stringify(requestBody)
    })

    if (!response.ok) {
      throw new Error(await errorMessage(response, 'Failed to fetch industries'))
    }

    const json = (await response.json()) as { data?: unknown[]; has_more?: boolean; count?: number }
    const rawRows = Array.isArray(json.data) ? json.data : []

    // Validate every row before it reaches the screen (CLAUDE.md). Drop (with a
    // dev warning) any row that fails rather than failing the whole grid.
    const validated: IndustryGridModel[] = []
    for (const row of rawRows) {
      try {
        validated.push(validateIndustryGridModel(row))
      } catch (e) {
        if (process.env.NODE_ENV === 'development') {
          console.warn('Industry row failed validation:', e, row)
        }
      }
    }

    return {
      data: validated,
      hasMore: Boolean(json.has_more),
      totalCount: typeof json.count === 'number' ? json.count : validated.length
    }
  } catch (error) {
    return {
      data: [],
      hasMore: false,
      totalCount: 0,
      error: error instanceof Error ? error.message : 'An unexpected error occurred while fetching industries'
    }
  }
}

export const createIndustry = async (name: string, accessToken: string | null): Promise<WriteResponse> => {
  try {
    const response = await fetch(buildFullUrl(BACKEND_BASE_URL, API_ROUTES.CONFIG.INDUSTRIES_CREATE), {
      method: API_ROUTES.CONFIG.INDUSTRIES_CREATE.method,
      headers: authHeaders(accessToken),
      body: JSON.stringify({ data: { name } })
    })
    if (!response.ok) return { success: false, error: await errorMessage(response, 'Failed to create industry') }
    const json = (await response.json()) as { data?: unknown }
    return { success: true, data: json.data }
  } catch (error) {
    return { success: false, error: error instanceof Error ? error.message : 'An unexpected error occurred' }
  }
}

export const renameIndustry = async (
  industryId: string,
  name: string,
  accessToken: string | null
): Promise<WriteResponse> => {
  try {
    const response = await fetch(
      buildFullUrl(BACKEND_BASE_URL, API_ROUTES.CONFIG.INDUSTRIES_UPDATE, { industryId }),
      {
        method: API_ROUTES.CONFIG.INDUSTRIES_UPDATE.method,
        headers: authHeaders(accessToken),
        body: JSON.stringify({ data: { name } })
      }
    )
    if (!response.ok) return { success: false, error: await errorMessage(response, 'Failed to rename industry') }
    const json = (await response.json()) as { data?: unknown }
    return { success: true, data: json.data }
  } catch (error) {
    return { success: false, error: error instanceof Error ? error.message : 'An unexpected error occurred' }
  }
}

export const deleteIndustry = async (industryId: string, accessToken: string | null): Promise<WriteResponse> => {
  try {
    const response = await fetch(
      buildFullUrl(BACKEND_BASE_URL, API_ROUTES.CONFIG.INDUSTRIES_DELETE, { industryId }),
      {
        method: API_ROUTES.CONFIG.INDUSTRIES_DELETE.method,
        headers: authHeaders(accessToken)
      }
    )
    if (!response.ok) return { success: false, error: await errorMessage(response, 'Failed to delete industry') }
    const json = (await response.json()) as { data?: unknown }
    return { success: true, data: json.data }
  } catch (error) {
    return { success: false, error: error instanceof Error ? error.message : 'An unexpected error occurred' }
  }
}

export interface IndustryAccountRow {
  accounts__id: string
  accounts__name: string
  accounts__industry: string | null
  accounts__industry__label: string | null
  accounts__roles_label: string
}

export const fetchIndustryAccounts = async (
  industryId: string,
  accessToken: string | null
): Promise<{ data: IndustryAccountRow[]; error?: string }> => {
  try {
    const response = await fetch(
      buildFullUrl(BACKEND_BASE_URL, API_ROUTES.CONFIG.INDUSTRY_ACCOUNTS, { industryId }),
      { method: API_ROUTES.CONFIG.INDUSTRY_ACCOUNTS.method, headers: authHeaders(accessToken) }
    )
    if (!response.ok) return { data: [], error: await errorMessage(response, 'Failed to fetch accounts') }
    const json = (await response.json()) as { data?: IndustryAccountRow[] }
    return { data: Array.isArray(json.data) ? json.data : [] }
  } catch (error) {
    return { data: [], error: error instanceof Error ? error.message : 'An unexpected error occurred' }
  }
}

export const updateAccountIndustry = async (
  accountId: string,
  industryId: string,
  accessToken: string | null
): Promise<WriteResponse> => {
  try {
    const response = await fetch(
      buildFullUrl(BACKEND_BASE_URL, API_ROUTES.CONFIG.ACCOUNT_INDUSTRY_UPDATE, { accountId }),
      {
        method: API_ROUTES.CONFIG.ACCOUNT_INDUSTRY_UPDATE.method,
        headers: authHeaders(accessToken),
        body: JSON.stringify({ data: { industry_id: industryId } })
      }
    )
    if (!response.ok) return { success: false, error: await errorMessage(response, 'Failed to update industry') }
    const json = (await response.json()) as { data?: unknown }
    return { success: true, data: json.data }
  } catch (error) {
    return { success: false, error: error instanceof Error ? error.message : 'An unexpected error occurred' }
  }
}

export const reassignIndustries = async (
  sourceIds: string[],
  targetId: string,
  deleteSources: boolean,
  accessToken: string | null
): Promise<WriteResponse> => {
  try {
    const response = await fetch(buildFullUrl(BACKEND_BASE_URL, API_ROUTES.CONFIG.INDUSTRIES_REASSIGN), {
      method: API_ROUTES.CONFIG.INDUSTRIES_REASSIGN.method,
      headers: authHeaders(accessToken),
      body: JSON.stringify({
        data: { source_ids: sourceIds, target_id: targetId, delete_sources: deleteSources }
      })
    })
    if (!response.ok) return { success: false, error: await errorMessage(response, 'Failed to reassign industries') }
    const json = (await response.json()) as { data?: unknown }
    return { success: true, data: json.data }
  } catch (error) {
    return { success: false, error: error instanceof Error ? error.message : 'An unexpected error occurred' }
  }
}
